import React, { useState, useEffect } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { appointmentService } from '../../../services/appointmentService';

export default function AppointmentCalendar({ 
  selectedAgent,
  selectedDate, 
  selectedTime, 
  onSelectDate, 
  onSelectTime 
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState('');

  useEffect(() => {
    if (selectedDate && selectedAgent) {
      loadAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [selectedDate, selectedAgent]);

  const loadAvailableSlots = async () => {
    try {
      setLoadingSlots(true);
      setSlotsError('');

      const slots = await appointmentService.getAvailableTimeSlots(
        selectedAgent.id,
        selectedDate
      );

      setAvailableSlots(slots || []);
    } catch (error) {
      console.error('Error loading time slots:', error);
      setSlotsError('Failed to load available times.');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isDateSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Choose Date & Time
      </h2>

      <p className="text-gray-600 mb-8">
        Select your preferred meeting date and time. All times are in Pacific Time (PT).
        {selectedAgent && (
          <span className="block mt-1 text-blue-600 font-medium">
            Showing availability for {selectedAgent.full_name}
          </span>
        )}
      </p>

      <div className="grid lg:grid-cols-2 gap-8">

        {/* Calendar */}
        <div>
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {monthName}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() - 1
                      )
                    )
                  }
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ‹
                </button>
                <button
                  onClick={() =>
                    setCurrentMonth(
                      new Date(
                        currentMonth.getFullYear(),
                        currentMonth.getMonth() + 1
                      )
                    )
                  }
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ›
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {days.map((date, index) => (
                <button
                  key={index}
                  onClick={() => date && !isDateDisabled(date) && onSelectDate(date)}
                  disabled={isDateDisabled(date)}
                  className={`aspect-square p-2 rounded-lg text-sm font-medium
                    ${
                      !date
                        ? 'invisible'
                        : isDateDisabled(date)
                        ? 'text-gray-300 cursor-not-allowed'
                        : isDateSelected(date)
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }
                  `}
                >
                  {date?.getDate()}
                </button>
              ))}
            </div>
          </div>

          {selectedDate && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                Selected: {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          )}
        </div>

        {/* Time Slots */}
        <div>
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Available Times</h3>
            </div>

            {loadingSlots ? (
              <div className="text-center py-6 text-gray-500">
                Loading available times...
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                No available times for this date.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  {availableSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      disabled={!slot.available}
                      onClick={() => onSelectTime(slot.timeString)}
                      className={`relative px-4 py-2 rounded-lg border text-sm font-medium transition-all
                        ${
                          selectedTime === slot.timeString
                            ? "bg-blue-600 text-white border-blue-600 shadow-md"
                            : slot.available
                            ? "bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-400"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }
                      `}
                    >
                      {slot.timeString}

                      {slot.available && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                      )}
                    </button>
                  ))}
                </div>

                {selectedTime && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Selected: {selectedTime} PT
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
